import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import Navbar from "../components/Navbar";
import AuthMedia from "../components/AuthMedia";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Input } from "../components/ui/input";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../components/ui/alert-dialog";
import { ShieldCheck, FileText, Clock, CheckCircle2, XCircle, Loader2, Users, KeyRound } from "lucide-react";
import { toast } from "sonner";

const STATUS_META = {
  declared: { label: "Dichiarata dai tutori", cls: "text-[#8a4a35] bg-[#FBEDE7]", icon: Users },
  pending_verification: { label: "In attesa di verifica", cls: "text-[#c9694f] bg-[#FBEDE7]", icon: Clock },
  verified: { label: "Verificata", cls: "text-[#4e8f6f] bg-[#EAF3EE]", icon: CheckCircle2 },
  rejected: { label: "Rifiutata", cls: "text-destructive bg-[#FBE9E9]", icon: XCircle },
};

const AdminVerifications = () => {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(null);
  const [reason, setReason] = useState("");
  const [gate, setGate] = useState({ enabled: false, code: "" });
  const [savingGate, setSavingGate] = useState(false);

  const load = () => api.get("/admin/verifications").then((res) => setItems(res.data)).catch(() => setItems([]));
  useEffect(() => {
    load();
    api.get("/admin/access-gate").then((r) => setGate(r.data)).catch(() => {});
  }, []);

  const saveGate = async (next) => {
    setSavingGate(true);
    try {
      const { data } = await api.put("/admin/access-gate", { enabled: next.enabled, code: next.code });
      setGate({ enabled: data.enabled, code: data.code });
      toast.success(data.enabled ? "Accesso su invito attivato" : "Accesso su invito disattivato — Eterna è aperta a tutti");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore");
    } finally { setSavingGate(false); }
  };

  const approve = async (id) => {
    setBusy(id);
    try {
      await api.post(`/admin/verifications/${id}/approve`);
      toast.success("Verifica approvata. Le volontà sono state rilasciate.");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore");
    } finally { setBusy(null); }
  };

  const reject = async (id) => {
    setBusy(id);
    try {
      await api.post(`/admin/verifications/${id}/reject`, { reason });
      toast.success("Certificato rifiutato.");
      setReason("");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore");
    } finally { setBusy(null); }
  };

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="max-w-4xl mx-auto px-6 py-10 fade-in">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-xl bg-[#FBEDE7] flex items-center justify-center">
            <ShieldCheck className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h1 className="font-serif text-4xl tracking-tight">Verifiche ufficiali</h1>
            <p className="text-muted-foreground mt-1">Esamina i certificati di morte caricati e approva il rilascio delle volontà.</p>
          </div>
        </div>

        {/* Access gate (fase di prova) */}
        <div className="bg-white rounded-2xl border border-border p-6 mb-8" data-testid="access-gate-card">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#EEF3EC] flex items-center justify-center shrink-0">
                <KeyRound className="w-5 h-5 text-[#4b8f6f]" />
              </div>
              <div>
                <h2 className="font-serif text-xl">Accesso su invito (fase di prova)</h2>
                <p className="text-sm text-muted-foreground mt-0.5 max-w-md">Quando attivo, solo chi conosce il codice d'invito può registrarsi. A fine prova, disattivalo per aprire Eterna a tutti.</p>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className="text-sm text-muted-foreground">{gate.enabled ? "Attivo" : "Disattivo"}</span>
              <Switch checked={gate.enabled} disabled={savingGate} onCheckedChange={(v) => saveGate({ enabled: v, code: gate.code })} data-testid="access-gate-switch" />
            </div>
          </div>
          <div className="flex items-end gap-3 mt-4 flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <label className="text-xs text-muted-foreground mb-1.5 block">Codice d'invito</label>
              <Input value={gate.code} onChange={(e) => setGate({ ...gate, code: e.target.value })} placeholder="es. ETERNA2026" className="bg-[#F4F1EA] border-border rounded-xl h-11" data-testid="access-gate-code-input" />
            </div>
            <Button onClick={() => saveGate({ enabled: gate.enabled, code: gate.code })} disabled={savingGate} className="rounded-full bg-primary hover:bg-[#c9694f] text-white h-11 px-6" data-testid="access-gate-save-btn">
              {savingGate ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} Salva codice
            </Button>
          </div>
        </div>

        {items === null ? (
          <p className="text-muted-foreground">Caricamento…</p>
        ) : items.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-3xl border border-border" data-testid="admin-empty">
            <ShieldCheck className="w-8 h-8 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">Nessun caso da verificare al momento.</p>
          </div>
        ) : (
          <div className="space-y-5">
            {items.map((it) => {
              const sm = STATUS_META[it.death_status] || STATUS_META.declared;
              const SIcon = sm.icon;
              const cert = it.certificate;
              return (
                <div key={it.id} className="bg-white rounded-2xl border border-border p-7" data-testid={`admin-case-${it.id}`}>
                  <div className="flex items-start justify-between flex-wrap gap-3">
                    <div>
                      <h3 className="font-serif text-2xl">{it.name}</h3>
                      <p className="text-sm text-muted-foreground">{it.email}</p>
                    </div>
                    <span className={`inline-flex items-center gap-1.5 text-xs px-3 py-1 rounded-full ${sm.cls}`} data-testid={`admin-status-${it.id}`}>
                      <SIcon className="w-3.5 h-3.5" /> {sm.label}
                    </span>
                  </div>

                  <div className="flex items-center gap-2 text-sm text-muted-foreground mt-3">
                    <Users className="w-4 h-4" /> {it.confirmed} di {it.required} conferme dei tutori
                  </div>

                  {cert ? (
                    <div className="bg-[#F4F1EA] rounded-xl p-4 mt-4">
                      <p className="text-sm font-medium text-[#2C2A29] mb-1 flex items-center gap-2">
                        <FileText className="w-4 h-4 text-primary" /> Certificato — Comune di {cert.comune}
                      </p>
                      <p className="text-xs text-muted-foreground mb-3">Caricato da {cert.uploaded_by} ({cert.uploaded_by_role}) · {new Date(cert.uploaded_at).toLocaleString("it-IT")}</p>
                      {cert.kind === "image"
                        ? <AuthMedia item={cert} className="w-full max-h-96 object-contain rounded-lg bg-white" />
                        : <AuthMedia item={cert} />}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground mt-4">Nessun certificato ancora caricato.</p>
                  )}

                  {it.rejection_reason && it.death_status === "rejected" && (
                    <p className="text-sm text-destructive mt-3">Motivo del rifiuto: {it.rejection_reason}</p>
                  )}

                  {it.death_status === "pending_verification" && (
                    <div className="flex flex-wrap gap-3 mt-5">
                      <Button onClick={() => approve(it.id)} disabled={busy === it.id} className="rounded-full bg-[#4e8f6f] hover:bg-[#3f7a5c] text-white h-11 px-6" data-testid={`admin-approve-${it.id}`}>
                        {busy === it.id ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />} Approva e rilascia
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="outline" className="rounded-full h-11 px-6 border-destructive/40 text-destructive hover:bg-[#FBE9E9]" data-testid={`admin-reject-${it.id}`}>
                            <XCircle className="w-4 h-4 mr-2" /> Rifiuta
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="bg-white rounded-2xl">
                          <AlertDialogHeader>
                            <AlertDialogTitle className="font-serif text-2xl">Rifiuta il certificato</AlertDialogTitle>
                            <AlertDialogDescription>Indica il motivo del rifiuto. Il caricatore potrà caricare un nuovo certificato.</AlertDialogDescription>
                          </AlertDialogHeader>
                          <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Motivo del rifiuto" className="w-full bg-[#F4F1EA] border border-border rounded-xl h-11 px-4 text-sm outline-none" data-testid={`admin-reject-reason-${it.id}`} />
                          <AlertDialogFooter>
                            <AlertDialogCancel className="rounded-full">Annulla</AlertDialogCancel>
                            <AlertDialogAction onClick={() => reject(it.id)} className="rounded-full bg-destructive hover:bg-[#b81e2f] text-white" data-testid={`admin-reject-confirm-${it.id}`}>Rifiuta</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminVerifications;
